import { useMutation, useQuery } from '@apollo/client'
import { faMessageLines } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  CreateSheetThreadCommentDocument,
  SheetThreadCommentNotificationsDocument,
  SheetThreadsDocument,
  type ViewFieldsFragment,
} from '@salescore/client-api'
import { useMe } from '@salescore/client-common'
import {
  closeRecordComments,
  closeThread,
  openRecordComments,
  openThread,
  selectThreadStatesForRecord,
  useAppDispatch,
  useAppSelector,
} from '@salescore/client-redux'
import { CORE_CONSTANT, type FieldType } from '@salescore/core'
import { organizationHasFeature } from '@salescore/frontend-common'
import { Badge, Divider, Form, List, Popover, Tooltip } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import { type KeyboardEventHandler, type PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilState, useRecoilValue } from 'recoil'

import { getBackgroundViewId } from '../../domain/miscSelectors'
import { useSharedUrl } from '../../hooks/useSharedUrl'
import { kpiParameterAtom, sheetPickedPresetNameAtom } from '../../recoil/navigation/atoms'
import { additionalConfigAtom } from '../../recoil/records/atoms'
import { viewsContextAtom } from '../../recoil/view/atoms'
import { useViewConfigSheet } from '../../recoil/viewConfig'
import { detectSharedUrlSettings } from '../view_ui/common'
import { CommentForm } from './CommentForm'
import { fieldTypeSchema, SheetThreadForm } from './SheetThreadForm'

export function SheetThreadsFormPopoverIfAllowed({
  recordId,
  recordName,
  children,
  setIsCommentActive,
  view,
}: PropsWithChildren<{
  recordId: string | undefined
  recordName: string | undefined
  /** 行にコメント入力中かの判定 */
  setIsCommentActive: (isActive: boolean) => void
  view: ViewFieldsFragment
}>) {
  const viewsContext = useRecoilValue(viewsContextAtom)
  const backgroundViewId = getBackgroundViewId(view.id, viewsContext.prevView)
  const me = useMe()
  const organizationId = me.organization.id
  const config = useViewConfigSheet()
  const sharedUrl = useSharedUrl()
  const dispatch = useAppDispatch()
  const { data } = useQuery(SheetThreadsDocument, {
    variables: {
      organizationId,
      recordIds: recordId === undefined ? [] : [recordId],
    },
    fetchPolicy: 'cache-only',
  })
  const threads = data?.sheetThreads ?? []
  const sheetThreadCommentNotificationsQueryData = useQuery(SheetThreadCommentNotificationsDocument, {
    variables: {
      organizationId,
    },
  })
  const sheetThreadCommentNotifications =
    sheetThreadCommentNotificationsQueryData.data?.sheetThreadCommentNotifications ?? []
  const [hoveredOnChildren, setHoveredOnChildren] = useState(false)
  const [popoverIsOpen, setPopoverIsOpen] = useState(false) // コメントのPopover表示
  const views = useRecoilValue(viewsContextAtom)
  const [kpiPivotParameter] = useRecoilState(kpiParameterAtom)
  const { kpiPivotPresetName } = useRecoilValue(viewsContextAtom)
  const [sheetPresetName] = useRecoilState(sheetPickedPresetNameAtom)
  const additionalConfig = useRecoilValue(additionalConfigAtom)
  const [createSheetThreadComment] = useMutation(CreateSheetThreadCommentDocument)
  const [form] = Form.useForm()
  const commentReferences = useRef(new Map<string, HTMLElement>())
  const [alreadyScrolledToComment, setAlreadyScrolledToComment] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const threadStates = useAppSelector((state) => selectThreadStatesForRecord(state, { viewId: view.id, recordId }))
  const { t } = useTranslation()

  const isSharedUrlSettings = detectSharedUrlSettings(config, sharedUrl)

  useEffect(() => {
    const sharedUrlSheetThreadComment = sharedUrl?.sheetThreadComment
    if (isSharedUrlSettings && isNotNullish(sharedUrlSheetThreadComment) && !alreadyScrolledToComment) {
      const commentListItem = commentReferences.current.get(sharedUrlSheetThreadComment.id)
      if (commentListItem !== undefined) {
        commentListItem.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setAlreadyScrolledToComment(true)
      }
    }
    // eslint-disable-next-line react-hooks/refs
  }, [isSharedUrlSettings, sharedUrl, commentReferences.current.size, alreadyScrolledToComment])

  const hasThreads = threads.length > 0

  const hasNotReadComments =
    recordId !== undefined &&
    sheetThreadCommentNotifications
      .filter((n) => n.sheetThreadComment.sheetThread.recordId === recordId)
      .some((notification) => notification.status === 'notRead')
  const status = hasNotReadComments ? 'error' : 'default'
  const commentFeatureEnabled = organizationHasFeature(me.organization, 'enable_slack_comment')

  if (
    !commentFeatureEnabled ||
    recordId === undefined ||
    view.id.startsWith(CORE_CONSTANT.KPI_PREVIEW_DRILL_DOWN_VIEW_ID_PREFIX) || // KPIプレビューの時
    view.id === '' || // 目標設定の時
    views.views[0]?.type === 'kpi' || // KPI タブ内の場合
    config.tree?.modelName === undefined
  ) {
    return <>{children}</>
  }

  async function onFinish(fields: FieldType) {
    if (isSubmitting) {
      return
    }
    try {
      const { text } = fieldTypeSchema.parse(fields)
      if (backgroundViewId === undefined) {
        return
      }
      setIsSubmitting(true)
      if (!commentFeatureEnabled || recordId === undefined || config.tree?.modelName === undefined) {
        return null
      }
      // eslint-disable-next-line sonarjs/no-dead-store,sonarjs/no-unused-vars
      const { data } = await createSheetThreadComment({
        variables: {
          sheetThreadCommentCreateInput: {
            viewId: backgroundViewId,
            organizationId,
            threadId: undefined, // 新しいスレッドを作成する
            recordId,
            modelName: config.tree.modelName,
            recordName,
            message: text,
            sheetViewId: view.id,
            kpiPivotParameter,
            kpiPivotPresetName,
            sheetPresetName,
            additionalConfigFilterLeafs: additionalConfig.filterLeafs,
          },
        },
        refetchQueries: [
          {
            query: SheetThreadsDocument,
            variables: {
              organizationId,
              recordIds: [recordId],
            },
            fetchPolicy: 'network-only',
          },
        ],
      })
    } catch {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: エラー処理
    } finally {
      setIsSubmitting(false)
    }
    form.resetFields()
    setIsSubmitting(false)
  }

  const handleKeyDown: KeyboardEventHandler<HTMLFormElement> = (event) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      form.submit()
    }
  }

  return (
    <Popover
      placement="rightTop"
      content={
        <div className="max-h-96 w-96 overflow-y-auto">
          <List>
            {threads.map((sheetThread) => (
              <List.Item key={sheetThread.id} className="!border-none !py-0">
                <SheetThreadForm
                  commentRefs={commentReferences}
                  recordId={recordId}
                  recordName={recordName}
                  sheetThread={sheetThread}
                  open={threadStates?.threadStates[sheetThread.id]?.open === true}
                  onOpenChange={(open) => {
                    if (open) {
                      dispatch(
                        openThread({
                          viewId: view.id,
                          recordId,
                          threadId: sheetThread.id,
                        }),
                      )
                    } else {
                      dispatch(
                        closeThread({
                          viewId: view.id,
                          recordId,
                          threadId: sheetThread.id,
                        }),
                      )
                    }
                  }}
                  view={view}
                />
              </List.Item>
            ))}
          </List>
          <Divider className="my-1" />
          <Form
            form={form}
            onFinish={(values) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              void onFinish(values)
            }}
            onKeyDown={handleKeyDown}
          >
            <CommentForm
              isReply={false}
              isSmall={false}
              isDisabled={false}
              onClickCancelButton={() => {
                dispatch(
                  closeRecordComments({
                    viewId: view.id,
                    recordId,
                  }),
                )
              }}
            />
          </Form>
        </div>
      }
      onOpenChange={(open) => {
        if (open) {
          dispatch(
            openRecordComments({
              viewId: view.id,
              recordId,
              threads,
            }),
          )
        } else {
          dispatch(
            closeRecordComments({
              viewId: view.id,
              recordId,
            }),
          )
        }
        setPopoverIsOpen(open)
        setIsCommentActive(open || hoveredOnChildren)
      }}
      open={threadStates?.open === true}
      trigger={['click']}
    >
      <Tooltip
        placement="topLeft"
        title={
          threads.length > 0
            ? t(`{{count}} 件のコメントがあります`, {
                count: threads.reduce((sum, thread) => sum + thread.comments.length, 0),
              })
            : t('コメントする')
        }
        onOpenChange={(open) => {
          setHoveredOnChildren(open)
          setIsCommentActive(open || popoverIsOpen)
        }}
      >
        <Badge dot={hasThreads || hasNotReadComments} status={status} offset={[-2, -2]}>
          {hoveredOnChildren ? (
            <FontAwesomeIcon icon={faMessageLines} size="lg" className="-mt-1 cursor-pointer" />
          ) : (
            <div className="size-4">{children}</div>
          )}
        </Badge>
      </Tooltip>
    </Popover>
  )
}
